import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import LotusIcon from "@/components/icons/LotusIcon";
import { Target, Briefcase, BookOpen, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import vinodProfile from "@/assets/vinod-profile.jpg";

const About = () => {
  return (
    <div className="min-h-screen" data-testid="about-page">
      <Header />

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-primary/10 to-background">
        <div className="container text-center">
          <h1 className="text-4xl md:text-5xl font-serif text-primary mb-4 font-bold">About Us</h1>
          <p className="text-lg text-foreground/70 max-w-2xl mx-auto font-medium">
            Meet the founder VINOD behind Siwon Galaxy's transformative journey
          </p>
        </div>
      </section>

      {/* Founder Profile Section */}
      <section className="py-16 bg-background">
        <div className="container">
          <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
            <Avatar className="h-40 w-40 md:h-48 md:w-48 border-4 border-accent shadow-lg mb-8">
              <AvatarImage src={vinodProfile} alt="Vinod Kumar - Founder of Siwon Galaxy" />
              <AvatarFallback className="text-3xl font-serif text-primary">VK</AvatarFallback>
            </Avatar>
            <h2 className="text-3xl md:text-4xl font-serif text-primary mb-2 font-bold">Vinod Kumar</h2>
            <p className="text-secondary font-semibold mb-6">
              Founder & Relationship and Life WISDOM Coach
            </p>
            <p className="text-foreground/80 text-lg leading-relaxed font-medium">
              Vinod Kumar transitioned from a high-achieving Chartered Accountant with 20 years of professional
              expertise to a passionate social researcher, Relationship and Life WISDOM Coach, and spiritual wisdom seeker. As
              Founder of the philanthropic Siwon Foundation, he now dedicates his life to harmonizing modern challenges
              with ancient Vedantic truths.
            </p>
          </div>
        </div>
      </section>

      {/* Lotus Divider */}
      <div className="lotus-divider container">
        <LotusIcon size={32} />
      </div>

      {/* Our Mission Section */}
      <section className="py-16 bg-muted/30 mandala-bg">
        <div className="container">
          <div className="card-spiritual max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <Target className="h-12 w-12 text-accent" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-primary mb-6 font-bold">Our Mission</h2>
            <p className="text-foreground/80 text-lg leading-relaxed mb-6 font-medium">
              Siwon Galaxy empowers working professionals and couples to transform relational discord and life
              stagnation into profound harmony. We fuse <strong className="text-primary">SIWON</strong> (Silence +
              Wonderment), the <strong className="text-primary">3 A's</strong> (Awareness, Alignment, Awakening), and{" "}
              <strong className="text-primary">Pancha Koshas</strong> for actionable, spiritually grounded coaching.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="px-4 py-2 bg-accent/20 text-primary rounded-full text-sm font-semibold">
                SIWON Framework
              </span>
              <span className="px-4 py-2 bg-accent/20 text-primary rounded-full text-sm font-semibold">
                3 A's Methodology
              </span>
              <span className="px-4 py-2 bg-accent/20 text-primary rounded-full text-sm font-semibold">
                Pancha Koshas
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Vinod's Journey Section */}
      <section className="py-16 bg-background">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-serif text-primary text-center mb-12 font-bold">Vinod's Journey</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Corporate Mastery */}
            <div className="card-spiritual text-center">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-accent/20 rounded-full">
                  <Briefcase className="h-8 w-8 text-primary" />
                </div>
              </div>
              <h3 className="text-xl font-serif text-primary mb-3 font-bold">Corporate Mastery to Calling</h3>
              <p className="text-foreground/80 text-sm leading-relaxed font-medium">
                After decades crunching numbers and advising businesses, Vinod sought deeper purpose amid personal
                relational insights.
              </p>
            </div>

            {/* Social Research */}
            <div className="card-spiritual text-center">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-accent/20 rounded-full">
                  <Target className="h-8 w-8 text-primary" />
                </div>
              </div>
              <h3 className="text-xl font-serif text-primary mb-3 font-bold">Social Research & Philanthropy</h3>
              <p className="text-foreground/80 text-sm leading-relaxed font-medium">
                Founded Siwon Foundation to uplift communities through emotional intelligence and spiritual frameworks.
              </p>
            </div>

            {/* Coaching Expertise */}
            <div className="card-spiritual text-center">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-accent/20 rounded-full">
                  <BookOpen className="h-8 w-8 text-primary" />
                </div>
              </div>
              <h3 className="text-xl font-serif text-primary mb-3 font-bold">Coaching Expertise</h3>
              <p className="text-foreground/80 text-sm leading-relaxed font-medium">
                Integrates Bhagavad Gita, Upanishads, and modern psychology—delivering workshops, webinars, and
                assessments via Siwon Galaxy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lotus Divider */}
      <div className="lotus-divider container">
        <LotusIcon size={32} />
      </div>

      {/* Proven Framework Section */}
      <section className="py-16 bg-muted/30 mandala-bg">
        <div className="container text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-primary mb-6 font-bold">Proven Framework</h2>
          <p className="text-foreground/80 text-lg max-w-3xl mx-auto mb-8 leading-relaxed font-medium">
            Our tools reveal kosha imbalances, guiding you from blame games to mutual wonder, burnout to purposeful
            wisdom. Your journey starts here.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/discovery/relationship-quiz" className="btn-lime inline-flex items-center justify-center gap-2">
              Relationship Assessment
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to="/discovery/life-quiz" className="btn-lime-outline inline-flex items-center justify-center gap-2">
              Life Diagnostic
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer onStartQuiz={() => {}} />
    </div>
  );
};

export default About;
